import React, { useState, useEffect } from 'react';
import { fcmService } from 'services/fcmService';
import { FcmConfig, FcmPayload, FcmTestResult } from 'types/fcm.types';

// ============================================================
// ConfigFcmPage — Configuración de Notificaciones Push (FCM)
// Acceso: ADMIN
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const Toggle: React.FC<{ value: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
      ${value ? 'bg-blue-600' : 'bg-slate-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
      ${value ? 'translate-x-6' : 'translate-x-1'}`}/>
  </button>
);

const Field: React.FC<{
  label:     string;
  hint?:     string;
  required?: boolean;
  children:  React.ReactNode;
}> = ({ label, hint, required, children }) => (
  <div>
    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && <p className="text-slate-400 text-xs mt-1">{hint}</p>}
  </div>
);

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed';

const DEFAULT: FcmConfig = {
  habilitado:          false,
  credencialesPath:    '',
  proyectoId:          '',
  credencialesExisten: false,
};

const ConfigFcmPage: React.FC = () => {
  const [form,    setForm]    = useState<FcmConfig>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [testResult, setTestResult] = useState<FcmTestResult | null>(null);

  useEffect(() => {
    fcmService.get()
      .then(data => { setForm(data); })
      .catch(e => setError(e instanceof Error ? e.message : 'Error al cargar configuración FCM'))
      .finally(() => setLoading(false));
  }, []);

  const set = <K extends keyof FcmConfig>(field: K, value: FcmConfig[K]) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleGuardar = async () => {
    setSaving(true); setError(null); setSuccess(false); setTestResult(null);
    try {
      const payload: FcmPayload = {
        habilitado:       form.habilitado,
        credencialesPath: form.credencialesPath,
        proyectoId:       form.proyectoId,
      };
      const updated = await fcmService.update(payload);
      setForm(updated);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleProbar = async () => {
    setTesting(true); setError(null); setSuccess(false); setTestResult(null);
    try {
      const result = await fcmService.test();
      setTestResult(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al probar la conexión');
    } finally {
      setTesting(false);
    }
  };

  const disabled = !form.habilitado;

  // Tono del banner de prueba según el resultado
  const testTone: 'success' | 'warning' | 'error' = !testResult
    ? 'success'
    : !testResult.firebaseInicializado
      ? 'error'
      : testResult.enviosExitosos > 0
        ? 'success'
        : 'warning';

  const toneCls: Record<typeof testTone, string> = {
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    error:   'bg-red-50 border-red-200 text-red-700',
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Notificaciones Push (FCM)</h1>
        <p className="text-blue-200 mt-1">Integración con Firebase Cloud Messaging para envío de notificaciones móviles</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">{error}</div>
      )}

      {/* Activar */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-slate-800">Habilitar push notifications móviles</p>
            <p className="text-xs text-slate-400 mt-0.5">Activa el envío de notificaciones móviles vía Firebase Cloud Messaging</p>
          </div>
          <Toggle value={form.habilitado} onChange={v => set('habilitado', v)} />
        </div>
      </div>

      {/* Credenciales del proyecto Firebase */}
      <div className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5 ${disabled ? 'opacity-60' : ''}`}>
        <h2 className="text-sm font-bold text-slate-700">Credenciales del proyecto Firebase</h2>
        <div className="grid grid-cols-1 gap-4">
          <Field
            label="Ruta del archivo de credenciales Firebase"
            required
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={form.credencialesPath}
                onChange={e => set('credencialesPath', e.target.value)}
                disabled={disabled}
                placeholder="/opt/sge/firebase-service-account.json"
                className={inputCls}
              />
              {form.credencialesPath && (
                form.credencialesExisten ? (
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold whitespace-nowrap">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd"/>
                    </svg>
                    Archivo encontrado
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold whitespace-nowrap">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                      <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clipRule="evenodd"/>
                    </svg>
                    No accesible
                  </span>
                )
              )}
            </div>
            <p className="text-slate-400 text-xs mt-1">
              Sube el archivo <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">firebase-service-account.json</code> al servidor (ej: <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">/opt/sge/firebase-service-account.json</code>) con permisos <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">600</code> y owner <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px]">sge</code>. Luego pega aquí la ruta absoluta. El cambio se aplica sin reiniciar.
            </p>
          </Field>

          <Field
            label="ID del proyecto Firebase"
            hint="Opcional. Identificador del proyecto Firebase. Lo encuentras en la consola de Firebase → Configuración del proyecto."
          >
            <input
              type="text"
              value={form.proyectoId}
              onChange={e => set('proyectoId', e.target.value)}
              disabled={disabled}
              placeholder="sge-adamsmith"
              className={inputCls}
            />
          </Field>
        </div>
      </div>

      {/* Resultado de la prueba */}
      {testResult && (
        <div className={`rounded-2xl border px-5 py-4 ${toneCls[testTone]}`}>
          <p className="text-sm font-bold">{testResult.mensaje}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
            <div>
              <div className="opacity-70">Firebase</div>
              <div className="font-bold">{testResult.firebaseInicializado ? 'Inicializado' : 'No inicializado'}</div>
            </div>
            <div>
              <div className="opacity-70">Dispositivos</div>
              <div className="font-bold">{testResult.dispositivosEncontrados}</div>
            </div>
            <div>
              <div className="opacity-70">Envíos OK</div>
              <div className="font-bold">{testResult.enviosExitosos}</div>
            </div>
            <div>
              <div className="opacity-70">Envíos fallidos</div>
              <div className="font-bold">{testResult.enviosFallidos}</div>
            </div>
          </div>
          {testResult.detalleError && (
            <pre className="mt-3 text-[11px] bg-white/60 rounded-lg px-3 py-2 whitespace-pre-wrap break-words">
              {testResult.detalleError}
            </pre>
          )}
        </div>
      )}

      {/* Footer con botones */}
      <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm px-6 py-4">
        <div>
          {success && (
            <span className="flex items-center gap-1.5 text-green-600 text-sm font-semibold">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd"/>
              </svg>
              Configuración guardada correctamente
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleProbar}
            disabled={testing || saving || disabled}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100
                       text-blue-700 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing && <SpinnerIcon />}
            Probar conexión
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving || testing}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-700 hover:bg-blue-800
                       text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-60"
          >
            {saving && <SpinnerIcon />}
            Guardar configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigFcmPage;
